// apps/web/src/main.tsx — Mobile-first Vite + React entry (Phase 2 plan 02-01).
//
// Phase 1 walking-skeleton: prove the data path from the React tree through
// the Vite dev proxy to the Fastify API to the seeded Postgres restaurant
// row, plus the DE/EN i18n toggle with localStorage persistence.
//
// Phase 2 extension: add router + AuthProvider + RequireAuth so the
// login flow (B1) → admin placeholder → logout loop is end-to-end
// testable. Phase 2 plan 02-04 wires the B6 settings hub + B6·B detail
// pages + day-exception modal nested routes under /admin/einstellungen/*.
//
// Decisions honoured:
//   D-04 — heading is the DB-driven restaurant name fetched via graphql-request.
//   D-05 — subtitle is the locale greeting "Hallo Gastio" / "Hello Gastio".
//   D-06 — i18n detection chain: localStorage["gastio.lang"] -> navigator.language -> "de".
//   D-07 — small pill toggle (DE / EN), tap-tap-tap, no select dropdown.
//   D-02 — access token in React memory only (managed by AuthProvider).
//   D-15 — X-Lang header on every GraphQL request (via lib/api.ts).

import "./index.css";

import React from "react";
import ReactDOM from "react-dom/client";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import { gql } from "graphql-request";
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import {
  BrowserRouter,
  Link,
  Outlet,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { deResources } from "./i18n/de";
import { enResources } from "./i18n/en";
import { AuthProvider } from "./lib/auth";
import { getGqlClient } from "./lib/api";
import { LoginPage } from "./pages/login";
import { RequireAuth } from "./components/RequireAuth";
import { AdminShell } from "./components/AdminShell";
import { SettingsHubPage } from "./pages/settings";
import { RestaurantDetailPage } from "./pages/settings/restaurant";
import { OpeningHoursPage } from "./pages/settings/opening-hours";
import { DayExceptionDialog } from "./pages/settings/DayExceptionDialog";
import { AvailabilityPage } from "./pages/settings/availability";
// simplify-brevo-sender-config: notifications admin page removed —
// sender fields now derived from Restaurant.name + Restaurant.email.
// The notification provider toggle (Brevo / SMTP) remains in JSONB;
// operators edit it via Prisma directly until a future follow-up
// resurfaces a simpler switch surface.
// --- 03-04 --- guest wizard routes (public, no auth)
import { DateStepPage } from "./pages/reservation/date-step";
import { SlotStepPage } from "./pages/reservation/slot-step";
// --- 04-03 --- guest wizard continuation (public, no auth)
import { A4SlotStep } from "./wizard/steps/A4SlotStep";
import { A5GuestStep } from "./wizard/steps/A5GuestStep";
import { A6ConfirmStep } from "./wizard/steps/A6ConfirmStep";
import { A7ConfirmationPage } from "./wizard/A7ConfirmationPage";
// --- 04-04 --- self-service surface (public, no auth).
// The A8 page reads the token from `useParams().token`; the
// modify + cancel + cancelled sub-routes use `?action=…` query
// params + a `/storniert` sub-path. Token is held in the
// in-memory store at `self-service/state.ts` (T-04-32: never
// localStorage).
import { SelfServicePage } from "./self-service/SelfServicePage";
import { SelfServiceModifyPage } from "./self-service/SelfServiceModifyPage";
import { SelfServiceCancelPage } from "./self-service/SelfServiceCancelPage";
import { SelfServiceCancelledPage } from "./self-service/SelfServiceCancelledPage";
// --- 05-04 + 260803-ger --- merged admin reservations surface
// (date picker + capacity + list/timeline + sort + search + filter +
// block-day + manual create) gated by AdminShell. The retired
// /admin/heute + /admin/kalender pages are mounted as durable
// authenticated redirects per CONTEXT.md D-01.
import { ReservationsPage } from "./pages/admin/reservations/ReservationsPage";
import { ReservationEditPage } from "./pages/admin/reservations/ReservationEditPage";
import { ManualCreatePage } from "./pages/admin/manual-create/ManualCreatePage";
// --- 07-06 --- signup flow + onboarding wizard (public, no auth).
// The signup / verify / onboarding pages wrap the onboarding JWT
// cookie as the credential; requireTenantMatch on the API side
// guards cross-tenant access. The routes live at the bare domain
// AND inside a /t/<slug>/ dev prefix; the TenantSlugProvider
// surfaces the slug to children for dev-mode banner rendering.
import { SignupPage } from "./pages/signup";
import { ConfirmRedirectPage } from "./pages/confirm/redirect";
import { VerifyTokenPage } from "./pages/verify/[token]";
import { OnboardingAddressPage } from "./pages/onboarding/[token]/address";
import { OnboardingCapacityPage } from "./pages/onboarding/[token]/capacity";
import { OnboardingHoursPage } from "./pages/onboarding/[token]/hours";
import { OnboardingReviewPage } from "./pages/onboarding/[token]/review";
import { TenantSlugProvider, getTenantBasename } from "./lib/tenant-resolver";
import { BaseDomainProvider } from "./lib/base-domain-context";
// --- 260920-u3p --- admin self-service email-change flow. The
// /admin/einstellungen/konto page is RBAC-gated (RequireAuth +
// AdminShell) and triggers requestAdminEmailChange. The
// /confirm-email-change/:token deeplink is PUBLIC (no auth, no CSRF
// — the token IS the credential, verified server-side via Argon2id
// PHC + sha256 lookup digest + single-use status flip). The
// persisted-ops gate accepts the 2 operations because
// scripts/build-persisted-ops.ts lists their operation files.
import { AdminAccountPage } from "./pages/admin/account";
import { ConfirmEmailChangePage } from "./pages/confirm-email-change/[token]";

// ----- i18n setup (D-06 detection chain) -----

const STORAGE_KEY = "gastio.lang";

function detectInitialLang(): "de" | "en" {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "de" || stored === "en") return stored;
  } catch {
    /* localStorage may be unavailable (private mode, SSR); fall through */
  }
  const nav = (
    typeof navigator !== "undefined" ? navigator.language : "de"
  ).toLowerCase();
  return nav.startsWith("en") ? "en" : "de";
}

void i18n.use(initReactI18next).init({
  resources: {
    de: { translation: deResources },
    en: { translation: enResources },
  },
  lng: detectInitialLang(),
  fallbackLng: "de",
  defaultNS: "translation",
  interpolation: { escapeValue: false },
  react: { useSuspense: false },
});

// Mirror the active language on <html lang> so the SettingsHubPage can
// read it for label rendering (Innen vs Indoor, Terrasse vs Terrace).
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
});
if (typeof document !== "undefined") {
  document.documentElement.lang = i18n.language;
}

// ----- Phase 1 landing page (legacy /) -----

const RESTAURANT_QUERY = gql`
  query RestaurantName {
    restaurantName
  }
`;

function LegacyLanding() {
  const { t, i18n: i18next } = useTranslation();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["restaurantName"],
    queryFn: async (): Promise<string | null> => {
      const res = await getGqlClient().request<{
        restaurantName: string | null;
      }>(RESTAURANT_QUERY);
      return res.restaurantName;
    },
  });

  const isEnglish = i18next.language.startsWith("en");
  const heading = isLoading ? "…" : isError ? "Fehler / Error" : (data ?? "—");

  return (
    <main className="min-h-screen bg-bg text-text font-body flex flex-col items-center justify-center px-4 py-8">
      <h1
        className="font-heading text-4xl md:text-5xl text-center mb-3"
        data-testid="restaurant-name"
      >
        {heading}
      </h1>
      <p className="text-base text-text/80 mb-6">{t("greeting")}</p>
      <button
        type="button"
        aria-label="toggle-language"
        data-testid="lang-toggle"
        onClick={() => {
          const next = isEnglish ? "de" : "en";
          void i18next.changeLanguage(next);
          try {
            window.localStorage.setItem(STORAGE_KEY, next);
          } catch {
            /* localStorage may be unavailable; toggle still works in-memory */
          }
        }}
        className="inline-flex items-center justify-center min-w-[3rem] px-3 py-1 rounded-full border border-divider font-heading text-sm hover:bg-surface"
      >
        {isEnglish ? "EN" : "DE"}
      </button>
      <Link
        to="/reservieren/datum"
        data-testid="reservation-cta"
        className="mt-4 inline-flex items-center justify-center px-5 py-2 rounded-full bg-accent text-bg font-heading text-base hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 transition-colors"
      >
        {t("reservationCta")}
      </Link>
    </main>
  );
}

// ----- Mount -----

const queryClient = new QueryClient();

// ----- 04-04: Self-service shell -----
//
// The `/reservierung/:token` parent route has three children that
// share the same path prefix:
//   - default (no `?action=`): A8 read-only view.
//   - `?action=modify`: A9 modify form.
//   - `?action=cancel`: A10 cancel form.
// The shell reads `useSearchParams().get("action")` and delegates
// to the matching page; an unknown value falls back to A8. The
// A11 confirmation has its own sub-path `/storniert` (mounted as
// a sibling in the <Routes> tree).
import { useParams, useSearchParams } from "react-router-dom";
import { isValidSelfServiceToken } from "./self-service/token-shape";
import { NotFoundCard } from "./self-service/NotFoundCard";
function SelfServiceShell(): JSX.Element {
  const [searchParams] = useSearchParams();
  const params = useParams<{ token: string }>();
  const action = searchParams.get("action");
  // Phase 4 follow-up #2: pre-flight check on the token shape.
  // The /reservierung/:token catch-all matches any 1..n character
  // URL segment, including obvious non-tokens like "datum",
  // "kontakt", "slot". The previous behaviour delegated those to
  // the read/modify/cancel pages, which fired Query.reservationByToken
  // and rendered the misleading "Link nicht mehr gültig" page.
  // We short-circuit at the shell so all three sub-routes share the
  // same protection and never spend a rate-limit bucket on a URL
  // that was never going to match a real reservation.
  if (!isValidSelfServiceToken(params.token)) {
    return <NotFoundCard />;
  }
  if (action === "modify") return <SelfServiceModifyPage />;
  if (action === "cancel") return <SelfServiceCancelPage />;
  return <SelfServicePage />;
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found in index.html");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={getTenantBasename()}>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<LegacyLanding />} />
            <Route path="/login" element={<LoginPage />} />
            {/* --- 03-04 --- public guest wizard routes (no RequireAuth) */}
            <Route path="/reservieren/datum" element={<DateStepPage />} />
            <Route path="/reservieren/zeit" element={<SlotStepPage />} />
            {/* --- 04-03 --- public guest wizard continuation (A4..A7).
                The plan's URL prefix is /reservierung/...; we use the
                existing /reservieren/... namespace to keep the guest
                frame consistent (the German noun is `Reservierung`,
                the existing convention is the verb `reservieren` —
                either is correct, the verb form is already wired). */}
            <Route path="/reservierung/slot" element={<A4SlotStep />} />
            <Route path="/reservierung/kontakt" element={<A5GuestStep />} />
            <Route
              path="/reservierung/uebersicht"
              element={<A6ConfirmStep />}
            />
            <Route
              path="/reservierung/bestaetigung"
              element={<A7ConfirmationPage />}
            />
            {/* --- 04-04 --- self-service surface. The A8 page is
                the read-only view (Query.reservationByToken); the
                A9 modify + A10 cancel sub-routes are differentiated
                by `?action=modify|cancel`; the A11 cancelled page
                is a sub-path `/storniert` on the same parent route.
                No RequireAuth — the token IS the credential
                (Argon2id-verified server-side per BOOK-06). */}
            <Route
              path="/reservierung/:token/storniert"
              element={<SelfServiceCancelledPage />}
            />
            <Route path="/reservierung/:token" element={<SelfServiceShell />} />
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <Navigate to="/admin/reservierungen" replace />
                </RequireAuth>
              }
            />
            {/* 260723-dvf + 260803-ger: every /admin/* route shares one
                authenticated layout that mounts AdminShell
                (responsive sidebar at md, fixed bottom-nav below).
                The shell renders the nested <Outlet/> for the
                descendant admin routes. After 260803-ger the only
                admin tabs are Reservierungen + Einstellungen — the
                retired /admin/heute + /admin/kalender routes are
                mounted as durable authenticated redirects to the
                merged /admin/reservierungen surface. */}
            <Route
              element={
                <RequireAuth>
                  <AdminShell>
                    <Outlet />
                  </AdminShell>
                </RequireAuth>
              }
            >
              {/* Legacy redirects — durable authenticated fallback so
                  old bookmarks + the post-create path in
                  ManualCreatePage stay reachable. */}
              <Route
                path="/admin/heute"
                element={<Navigate to="/admin/reservierungen" replace />}
              />
              <Route
                path="/admin/kalender"
                element={<Navigate to="/admin/reservierungen" replace />}
              />
              {/* Phase 5 plan 05-04 + 260803-ger: merged admin
                  reservations surface. */}
              <Route
                path="/admin/reservierungen"
                element={<ReservationsPage />}
              />
              <Route
                path="/admin/reservierungen/neu"
                element={<ManualCreatePage />}
              />
              <Route
                path="/admin/reservierungen/:id"
                element={<ReservationEditPage />}
              />
              {/* Phase 2 plan 02-04: settings hub + descendants. */}
              <Route
                path="/admin/einstellungen"
                element={<SettingsHubPage />}
              />
              <Route
                path="/admin/einstellungen/restaurant"
                element={<RestaurantDetailPage />}
              />
              <Route
                path="/admin/einstellungen/oeffnungszeiten"
                element={<OpeningHoursPage />}
              />
              <Route
                path="/admin/einstellungen/oeffnungszeiten/ausnahme"
                element={<DayExceptionDialog />}
              />
              <Route
                path="/admin/einstellungen/verfuegbarkeit"
                element={<AvailabilityPage />}
              />
              {/* Quick-Task 260920-u3p: admin self-service email-change
                  form (Konto / Account). The page fires
                  requestAdminEmailChange on submit. Rate-limited to
                  3 requests / 24h per adminUserId. */}
              <Route
                path="/admin/einstellungen/konto"
                element={<AdminAccountPage />}
              />
              {/* simplify-brevo-sender-config: per-restaurant notification
                  provider config page (/admin/einstellungen/benachrichtigungen)
                  removed — the sender identity is now derived automatically
                  from Restaurant.name (senderName) + Restaurant.email (reply-to)
                  with a Brevo-verified hardcoded sender address. Operators
                  flip Brevo ↔ SMTP ↔ none via Prisma JSONB directly. */}
            </Route>
            {/* --- 07-06 --- self-service signup + onboarding wizard.
                Exactly 7 public routes per W11 fix (signup, confirm,
                verify, 4 onboarding steps). The TenantSlugProvider
                exposes the dev-mode /t/<slug>/ prefix to children so
                the VerifyTokenPage can render the dev banner. None of
                these routes are wrapped in RequireAuth — the magic-
                link token (signup / verify) and the onboarding JWT
                (4 wizard steps) ARE the credentials, validated by the
                API's resolve-tenant + requireTenantMatch guards.
                Phase 8 plan 08-01 (D-08-01): the BaseDomainProvider
                wraps the same subtree so signup.tsx can read the
                build-time `__BASE_DOMAIN__` constant via
                useBaseDomain() and pass it as the `baseDomain`
                interpolation variable for the slug helper text. */}
            <Route
              path="/signup"
              element={
                <TenantSlugProvider>
                  <BaseDomainProvider>
                    <SignupPage />
                  </BaseDomainProvider>
                </TenantSlugProvider>
              }
            />
            <Route
              path="/confirm/:token"
              element={
                <TenantSlugProvider>
                  <BaseDomainProvider>
                    <ConfirmRedirectPage />
                  </BaseDomainProvider>
                </TenantSlugProvider>
              }
            />
            <Route
              path="/verify/:token"
              element={
                <TenantSlugProvider>
                  <BaseDomainProvider>
                    <VerifyTokenPage />
                  </BaseDomainProvider>
                </TenantSlugProvider>
              }
            />
            {/* Quick-Task 260920-u3p: public deeplink for confirming
                an admin email change. The page fires the mutation
                ONCE on mount — see CONTEXT.md BLOCKER-4. The token
                IS the credential; no JWT, no CSRF. On success the
                user is sent back to /admin/login. */}
            <Route
              path="/confirm-email-change/:token"
              element={<ConfirmEmailChangePage />}
            />
            <Route
              path="/onboarding/:token/address"
              element={
                <TenantSlugProvider>
                  <BaseDomainProvider>
                    <OnboardingAddressPage />
                  </BaseDomainProvider>
                </TenantSlugProvider>
              }
            />
            <Route
              path="/onboarding/:token/capacity"
              element={
                <TenantSlugProvider>
                  <BaseDomainProvider>
                    <OnboardingCapacityPage />
                  </BaseDomainProvider>
                </TenantSlugProvider>
              }
            />
            <Route
              path="/onboarding/:token/hours"
              element={
                <TenantSlugProvider>
                  <BaseDomainProvider>
                    <OnboardingHoursPage />
                  </BaseDomainProvider>
                </TenantSlugProvider>
              }
            />
            <Route
              path="/onboarding/:token/review"
              element={
                <TenantSlugProvider>
                  <BaseDomainProvider>
                    <OnboardingReviewPage />
                  </BaseDomainProvider>
                </TenantSlugProvider>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
